import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaTimes } from 'react-icons/fa';
import axios from 'axios';
import API_URL from '../config';
import { MAX_ATTACHMENTS, validateFileSelection } from '../utils/fileUploadValidation';
import ToastMessage from './ToastMessage';
import useToast from '../hooks/useToast';
import { useLanguage } from '../context/LanguageContext';
import { formatDateTime24 } from '../utils/dateTimeFormat';
import { getApiErrorMessage, getAuthHeaders, getStoredAccessToken, getStoredRefreshToken, handleExpiredSession, storeAuthSession } from '../utils/authSession';
import {
  addLocalDevNotification,
  addLocalDevSubtask,
  getLocalDevSubtasks,
  isLocalDevCompanyId,
  LOCAL_DEV_PROJECTS,
  LOCAL_DEV_USERS,
} from '../utils/localDevAuth';
const CreateTask = ({ user, setIsSidebarOpen }) => {
  const { t, locale } = useLanguage();
  const weekDays = [
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
    { value: 0, label: 'Sun' },
  ];
  const [isSidebarOpen, setIsSidebarOpenLocal] = useState(true);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [formData, setFormData] = useState({
    project_id: '',
    major_task_name: '',
    major_task_description: '',
    subtasks: [],
  });
  const [subtaskForm, setSubtaskForm] = useState({
    name: '',
    description: '',
    assigned_to_id: '',
    due_datetime: '',
    status: 'ongoing',
    priority: 'medium',
    is_daily_activity: false,
    recurring_days: [],
    assigned_files: [],
    assigned_links: [],
  });
  const [showMajorTaskForm, setShowMajorTaskForm] = useState(false);
  const [showSubtaskForm, setShowSubtaskForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [teamMembersWithTasks, setTeamMembersWithTasks] = useState([]);
  const navigate = useNavigate();
  const { toast, showToast, clearToast } = useToast();
  const isLocalDevSession = isLocalDevCompanyId(user?.company?.id) || isLocalDevCompanyId(localStorage.getItem('company_id'));

  useEffect(() => {
    if (setIsSidebarOpen) {
      setIsSidebarOpenLocal(isSidebarOpen);
    }
  }, [isSidebarOpen, setIsSidebarOpen]);

  const refreshToken = useCallback(async () => {
    try {
      const storedRefreshToken = getStoredRefreshToken();
      if (!storedRefreshToken) {
        throw new Error('No refresh token available');
      }
      const response = await axios.post(`${API_URL}/api/token/refresh/`, {
        refresh: storedRefreshToken,
      }, {
        __skipAuthRedirect: true,
      });
      storeAuthSession({
        accessToken: response.data.access,
        companyId: user?.company?.id || localStorage.getItem('company_id') || 1,
      });
      return response.data.access;
    } catch (error) {
      handleExpiredSession({ navigate, companyId: user?.company?.id || 1 });
      return null;
    }
  }, [user, navigate]);

  const fetchData = useCallback(async () => {
    try {
      let token = getStoredAccessToken();
      if (isLocalDevSession) {
        const localProjects = LOCAL_DEV_PROJECTS.filter(project =>
          project.company.id === user.company?.id &&
          (project.status === 'ongoing' || project.status === 'planned')
        );
        const localUsers = LOCAL_DEV_USERS.filter(localUser =>
          localUser.company.id === user.company?.id &&
          (localUser.role === 'team_member' || localUser.role === 'manager')
        );

        setProjects(localProjects);
        setUsers(localUsers);
        setFilteredUsers(localUsers);
        setError('');

        if (user.role === 'manager') {
          const localSubtasks = getLocalDevSubtasks();
          const teamMembersWithTaskCount = localUsers
            .filter(localUser => localUser.role === 'team_member')
            .map(member => {
              const tasks = localSubtasks.filter(task =>
                task.assigned_to?.id === member.id &&
                task.status === 'ongoing' &&
                task.major_task?.project?.company?.id === user.company?.id
              );
              return {
                id: member.id,
                email: member.email,
                tasks: tasks.map(task => task.name),
                taskCount: tasks.length,
              };
            })
            .sort((a, b) => a.taskCount - b.taskCount);

          setTeamMembersWithTasks(teamMembersWithTaskCount);
        }

        return;
      }
      if (!token) {
        handleExpiredSession({ navigate, companyId: user?.company?.id || 1 });
        return;
      }

      const config = { headers: getAuthHeaders(token) };
      let projectsRes, usersRes;
      try {
        [projectsRes, usersRes] = await Promise.all([
          axios.get(`${API_URL}/api/projects/`, config),
          axios.get(`${API_URL}/api/users/`, config),
        ]);
      } catch (error) {
        if (error.response?.status === 401) {
          token = await refreshToken();
          if (!token) return;
          config.headers = getAuthHeaders(token);
          [projectsRes, usersRes] = await Promise.all([
            axios.get(`${API_URL}/api/projects/`, config),
            axios.get(`${API_URL}/api/users/`, config),
          ]);
        } else {
          throw error;
        }
      }

      // Filter projects to only ongoing or planned (exclude completed)
      const projectsData = projectsRes.data.filter(project =>
        project.company.id === user.company.id &&
        (project.status === 'ongoing' || project.status === 'planned')
      );
      setProjects(projectsData);
const userArray = usersRes.data.filter(u => {
        const isRoleMatch = u.role === 'team_member' || u.role === 'manager';
        return isRoleMatch && u.company?.id === user.company?.id;
      });

      setUsers(userArray);
      setFilteredUsers(userArray);
      setError('');

      // Fetch team members and their ongoing tasks if manager
      if (user.role === 'manager') {
        const teamMembersRes = await axios.get(`${API_URL}/api/team-members/`, config);
        const managedTeamMembers = teamMembersRes.data.filter(tm => tm.manager.id === user.id);
        const teamMemberIds = managedTeamMembers.map(tm => tm.user.id);

        const ongoingSubtasksRes = await axios.get(`${API_URL}/api/subtasks/?project_status=ongoing`, config);
        const ongoingSubtasks = ongoingSubtasksRes.data.filter(st =>
          teamMemberIds.includes(st.assigned_to?.id) && st.status === 'ongoing'
        );

        const teamMembersWithTaskCount = teamMemberIds.map(id => {
          const member = userArray.find(u => u.id === id);
          const tasks = ongoingSubtasks.filter(st => st.assigned_to?.id === id);
          return {
            id: member.id,
            email: member.email,
            tasks: tasks.map(t => t.name),
            taskCount: tasks.length,
          };
        }).sort((a, b) => a.taskCount - b.taskCount);

        setTeamMembersWithTasks(teamMembersWithTaskCount);
      }
    } catch (error) {
setError('Failed to load data. Please try again.');
      if (error.response?.status === 401) {
        handleExpiredSession({ navigate, companyId: user?.company?.id || 1 });
      }
    } finally {
      setLoading(false);
    }
  }, [user, navigate, refreshToken, isLocalDevSession]);

  useEffect(() => {
if (user && user.id) {
      fetchData();
    } else {
      setLoading(false);
      setError('User data not available.');
    }
  }, [user, navigate, fetchData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubtaskInputChange = (e) => {
    const { name, value, files, type, checked } = e.target;
    if (name === 'assigned_files') {
      const incomingFiles = Array.from(files || []);
      setSubtaskForm(prev => {
        const validation = validateFileSelection(prev.assigned_files, incomingFiles);
        if (!validation.isValid) {
          showToast(validation.error, 'error');
          return prev;
        }
        setError('');
        return { ...prev, assigned_files: validation.files };
      });
      e.target.value = '';
      return;
    }
    setSubtaskForm(prev => {
      const nextValue = type === 'checkbox' ? checked : value;
      if (name === 'due_datetime' && value && prev.recurring_days.length > 0) {
        return {
          ...prev,
          due_datetime: formatForDateTimeLocal(getNextDateTimeForRecurringDays(value, prev.recurring_days)),
        };
      }
      return { ...prev, [name]: nextValue };
    });
  };

  const getDefaultDueDateTime = () => {
    const date = new Date();
    date.setHours(date.getHours() + 1, 0, 0, 0);
    return formatForDateTimeLocal(date);
  };

  const getDateTimeForWeekday = (baseDateTime, weekday) => {
    const baseDate = new Date(baseDateTime);
    const result = new Date(baseDate);
    const daysUntilWeekday = (weekday - baseDate.getDay() + 7) % 7;
    result.setDate(baseDate.getDate() + daysUntilWeekday);
    if (result <= new Date()) {
      result.setDate(result.getDate() + 7);
    }
    return result;
  };

  const getNextDateTimeForRecurringDays = (baseDateTime, recurringDays) => {
    if (!baseDateTime || recurringDays.length === 0) return baseDateTime;
    return recurringDays
      .map(dayValue => getDateTimeForWeekday(baseDateTime, dayValue))
      .sort((a, b) => a - b)[0];
  };

  const formatForDateTimeLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const toggleRecurringDay = (dayValue) => {
    setSubtaskForm(prev => {
      const isSelected = prev.recurring_days.includes(dayValue);
      const recurringDays = isSelected
        ? prev.recurring_days.filter(day => day !== dayValue)
        : [...prev.recurring_days, dayValue].sort((a, b) => a - b);
      const baseDueDateTime = prev.due_datetime || getDefaultDueDateTime();
      const nextDueDateTime = recurringDays.length > 0
        ? formatForDateTimeLocal(getNextDateTimeForRecurringDays(baseDueDateTime, recurringDays))
        : prev.due_datetime;
      return {
        ...prev,
        recurring_days: recurringDays,
        is_daily_activity: recurringDays.length > 0,
        due_datetime: nextDueDateTime,
      };
    });
  };

  const getRecurringDueDates = () => {
    if (!subtaskForm.due_datetime || subtaskForm.recurring_days.length === 0) return [];
    return subtaskForm.recurring_days
      .map(dayValue => {
        const dueDate = getDateTimeForWeekday(subtaskForm.due_datetime, dayValue);
        const day = weekDays.find(candidate => candidate.value === dayValue);
        return {
          dayValue,
          label: day?.label || '',
          due_datetime: formatForDateTimeLocal(dueDate),
        };
      })
      .sort((a, b) => new Date(a.due_datetime) - new Date(b.due_datetime));
  };

  const buildSubtasksToAdd = () => {
    if (subtaskForm.recurring_days.length > 0) {
      return getRecurringDueDates().map(({ dayValue, due_datetime }) => ({
        ...subtaskForm,
        is_daily_activity: true,
        assigned_to_id: Number(subtaskForm.assigned_to_id),
        recurring_days: [dayValue],
        due_datetime,
        assigned_links: subtaskForm.assigned_links.filter(link => link && link.trim()),
      }));
    }

    return [{
      ...subtaskForm,
      is_daily_activity: false,
      assigned_to_id: Number(subtaskForm.assigned_to_id),
      assigned_links: subtaskForm.assigned_links.filter(link => link && link.trim()),
    }];
  };

  const isSubtaskReady = (subtask) => Boolean(
    subtask.name?.trim() &&
    subtask.assigned_to_id &&
    subtask.due_datetime &&
    subtask.priority &&
    subtask.status &&
    new Date(subtask.due_datetime) > new Date()
  );

  const hasSubtaskDraftStarted = () => Boolean(
    subtaskForm.name.trim() ||
    subtaskForm.description.trim() ||
    assigneeSearch.trim() ||
    subtaskForm.assigned_to_id ||
    subtaskForm.due_datetime ||
    subtaskForm.recurring_days.length > 0 ||
    subtaskForm.assigned_files.length > 0 ||
    subtaskForm.assigned_links.some(link => link.trim())
  );

  const getFormInvalidReason = () => {
    if (!formData.project_id) return 'Please select project.';
    if (!formData.major_task_name.trim()) return 'Please enter major task name.';

    if (showSubtaskForm && (hasSubtaskDraftStarted() || formData.subtasks.length === 0)) {
      if (!subtaskForm.name.trim()) return 'Please enter subtask name.';
      if (!subtaskForm.assigned_to_id) return 'Please select assignee from search.';
      if (!subtaskForm.due_datetime) return 'Please select due date and time.';
      if (new Date(subtaskForm.due_datetime) <= new Date()) return 'Due date and time must be in future.';
    }

    const subtasksForSubmit = getSubtasksForSubmit();
    if (subtasksForSubmit.length === 0) return 'Please add at least one subtask.';

    const invalidSubtask = subtasksForSubmit.find(subtask => !isSubtaskReady(subtask));
    if (!invalidSubtask) return '';
    if (!invalidSubtask.name) return 'Please enter subtask name.';
    if (!invalidSubtask.assigned_to_id) return 'Please select assignee from search.';
    if (!invalidSubtask.due_datetime) return 'Please select due date and time.';
    if (new Date(invalidSubtask.due_datetime) <= new Date()) return 'Due date and time must be in future.';
    return 'Please complete all required subtask fields.';
  };

  const isCurrentSubtaskDraftReady = () => {
    if (!isSubtaskReady(subtaskForm)) return false;
    return true;
  };

  const getSubtasksForSubmit = () => {
    if (showSubtaskForm && isCurrentSubtaskDraftReady()) {
      return [...formData.subtasks, ...buildSubtasksToAdd()];
    }
    return formData.subtasks;
  };

  const removeSubtaskFile = (index) => {
    setSubtaskForm(prev => ({
      ...prev,
      assigned_files: prev.assigned_files.filter((_, i) => i !== index),
    }));
  };

  const addSubtaskLink = () => {
    setSubtaskForm(prev => {
      if (prev.assigned_links.length >= MAX_ATTACHMENTS) {
        const message = t('toast.maxLinksAllowed');
        setError(message);
        showToast(message, 'error');
        return prev;
      }
      setError('');
      return { ...prev, assigned_links: [...prev.assigned_links, ''] };
    });
  };

  const updateSubtaskLink = (index, value) => {
    setSubtaskForm(prev => ({
      ...prev,
      assigned_links: prev.assigned_links.map((link, i) => (i === index ? value : link)),
    }));
  };

  const removeSubtaskLink = (index) => {
    setSubtaskForm(prev => ({
      ...prev,
      assigned_links: prev.assigned_links.filter((_, i) => i !== index),
    }));
  };

  const getAssigneeName = (assignee) => {
    const firstName = assignee.first_name || assignee.user?.first_name || '';
    const lastName = assignee.last_name || assignee.user?.last_name || '';
    const fullName = assignee.full_name || `${firstName} ${lastName}`.trim();
    return fullName || assignee.name || assignee.email?.split('@')[0] || '';
  };

  const getAssigneeSearchText = (assignee) => [
    assignee.email,
    assignee.email?.split('@')[0],
    assignee.code,
    assignee.designation,
    getAssigneeName(assignee),
  ].filter(Boolean).join(' ').toLowerCase();

  const findExactAssignee = (searchTerm, candidates = users) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return null;

    return candidates.find(assignee => {
      const assigneeName = getAssigneeName(assignee).toLowerCase();
      const email = assignee.email?.toLowerCase();
      const emailName = email?.split('@')[0];
      const code = assignee.code?.toLowerCase();

      return (
        email === normalizedSearch ||
        emailName === normalizedSearch ||
        assigneeName === normalizedSearch ||
        code === normalizedSearch
      );
    }) || null;
  };

  const isAssignableUser = (candidate) => (
    (candidate.role === 'team_member' || candidate.role === 'manager') &&
    candidate.company?.id === user.company?.id
  );

  const handleAssigneeSearch = (searchTerm) => {
    setAssigneeSearch(searchTerm);
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filtered = users.filter(u => {
      const isSearchMatch = getAssigneeSearchText(u).includes(normalizedSearch);
      return isAssignableUser(u) && isSearchMatch;
    });
    const exactAssignee = findExactAssignee(searchTerm, filtered);
    setFilteredUsers(filtered);
    setSubtaskForm(prev => ({
      ...prev,
      assigned_to_id: exactAssignee ? exactAssignee.id : '',
    }));
  };

  const handleAssigneeBlur = () => {
    const exactAssignee = findExactAssignee(
      assigneeSearch,
      users.filter(isAssignableUser)
    );

    if (!exactAssignee) return;

    setAssigneeSearch(exactAssignee.email);
    setSubtaskForm(prev => ({
      ...prev,
      assigned_to_id: exactAssignee.id,
    }));
  };

  const addSubtask = () => {
    const { name, assigned_to_id, due_datetime, priority, status } = subtaskForm;

    if (!name || !assigned_to_id || !due_datetime || !priority || !status) {
      const message = t('toast.subtaskFieldsRequired');
      setError(message);
      showToast(message, 'error');
      return;
    }
    const selectedDateTime = new Date(subtaskForm.due_datetime);
    const now = new Date();

    if (selectedDateTime <= now) {
      const message = t('toast.dueFuture');
      setError(message);
      showToast(message, 'error');
      return;
    }

    setError('');
    const subtasksToAdd = buildSubtasksToAdd();

    setFormData(prev => ({
      ...prev,
      subtasks: [
        ...prev.subtasks,
        ...subtasksToAdd,
      ],
    }));

    setSubtaskForm({
      name: '',
      description: '',
      assigned_to_id: '',
      due_datetime: '',
      status: 'ongoing',
      priority: 'medium',
      is_daily_activity: false,
      recurring_days: [],
      assigned_files: [],
      assigned_links: [],
    });
    setAssigneeSearch('');
    setFilteredUsers(users);

    setShowSubtaskForm(false);
    showToast(t('toast.subtaskAdded'), 'success');
  };


  const removeSubtask = (index) => {
    setFormData(prev => ({
      ...prev,
      subtasks: prev.subtasks.filter((_, i) => i !== index),
    }));
  };
  const formInvalidReason = getFormInvalidReason();
  const isFormValid = !formInvalidReason;
  const recurringDueDates = getRecurringDueDates();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isCreatingTask) {
      showToast(t('toast.taskCreationInProgress'), 'info');
      return;
    }
    if (!isFormValid) {
      setError(formInvalidReason);
      showToast(formInvalidReason, 'error');
      return;
    }
    try {
      let token = getStoredAccessToken();
      const projectId = Number(formData.project_id);
      if (!projectId || projectId <= 0) {
        const message = t('toast.selectValidProject');
        setError(message);
        showToast(message, 'error');
        return;
      }
      setIsCreatingTask(true);
      showToast(t('toast.creatingTaskWait'), 'info');
      const subtasksForSubmit = getSubtasksForSubmit();

      if (isLocalDevSession) {
        const selectedProject = projects.find(project => project.id === projectId);
        const localMajorTask = {
          id: `local-major-task-${Date.now()}`,
          name: formData.major_task_name,
          description: formData.major_task_description,
          due_date: null,
          project: selectedProject,
        };

        subtasksForSubmit.forEach(subtask => {
          const assignee = users.find(candidate => candidate.id === Number(subtask.assigned_to_id));
          addLocalDevSubtask({
            majorTask: localMajorTask,
            subtask,
            assignedTo: assignee,
            assignedBy: user,
          });
          if (assignee?.email) {
            addLocalDevNotification({
              recipientEmail: assignee.email,
              type: 'task_assignment',
              message: `Subtask '${subtask.name}' assigned by ${user.email}.`,
              messageHi: `सबटास्क '${subtask.name}' ${user.email} द्वारा असाइन किया गया है।`,
              createdBy: user.email,
            });
          }
          if (user?.email) {
            addLocalDevNotification({
              recipientEmail: user.email,
              type: 'task_assignment',
              message: `Subtask '${subtask.name}' assigned to ${assignee?.email || 'assignee'}.`,
              messageHi: `सबटास्क '${subtask.name}' ${assignee?.email || 'असाइनी'} को असाइन किया गया है।`,
              createdBy: user.email,
            });
          }
        });
        setSuccess(t('toast.taskCreated'));
        showToast(t('toast.taskCreated'), 'success');
        window.dispatchEvent(new Event('notifications:refresh'));
        window.dispatchEvent(new Event('local-dev-tasks:refresh'));
        await fetchData();
        setError('');
        setFormData({
          project_id: '',
          major_task_name: '',
          major_task_description: '',
          subtasks: [],
        });
        setShowMajorTaskForm(false);
        setShowSubtaskForm(false);
        return;
      }

      let majorTaskDueDateTime = null;

      if (subtasksForSubmit.length > 0) {
        const dueTimes = subtasksForSubmit
          .filter(st => st.due_datetime)
          .map(st => new Date(st.due_datetime).getTime());

        if (dueTimes.length > 0) {
          majorTaskDueDateTime = new Date(Math.max(...dueTimes)).toISOString();
        }
      }

      const majorTaskPayload = {
        project_id: projectId,
        name: formData.major_task_name,
        description: formData.major_task_description,
        due_date: majorTaskDueDateTime, // backend receives datetime
      };
const config = {
        headers: {
          ...getAuthHeaders(token),
          'Content-Type': 'application/json',
        },
      };

      let majorTaskRes;
      try {
        majorTaskRes = await axios.post(
          `${API_URL}/api/major-tasks/`,
          majorTaskPayload,
          {
            ...config,
            timeout: 15000,
          }
        );
} catch (error) {
        if (error.response?.status === 401) {
          const newToken = await refreshToken();
          if (!newToken) return;
          token = newToken;
          majorTaskRes = await axios.post(
            `${API_URL}/api/major-tasks/`,
            majorTaskPayload,
            {
              headers: {
                ...getAuthHeaders(newToken),
                'Content-Type': 'application/json',
              },
              timeout: 15000,
            }
          );
} else {
          throw error;
        }
      }

      const majorTaskId = majorTaskRes.data.id;

      for (const subtask of subtasksForSubmit) {
        const subtaskPayload = new FormData();
        subtaskPayload.append('major_task', majorTaskId);
        subtaskPayload.append('name', subtask.name);
        subtaskPayload.append('description', subtask.description || '');
        subtaskPayload.append('assigned_to_id', subtask.assigned_to_id);
        subtaskPayload.append('due_date', subtask.due_datetime);
        subtaskPayload.append('status', subtask.status);
        subtaskPayload.append('priority', subtask.priority);
        subtaskPayload.append('is_daily_activity', subtask.is_daily_activity ? 'true' : 'false');
        if (subtask.assigned_files && subtask.assigned_files.length > 0) {
          subtask.assigned_files.forEach(file => {
            subtaskPayload.append('assigned_files', file);
          });
        }
        if (subtask.assigned_links && subtask.assigned_links.length > 0) {
          subtask.assigned_links.forEach(link => {
            if (link) {
              subtaskPayload.append('assigned_links', link);
            }
          });
        }

        await axios.post(`${API_URL}/api/subtasks/`, subtaskPayload, {
          headers: {
            ...getAuthHeaders(token),
            'Content-Type': 'multipart/form-data',
          },
          timeout: 15000,
        });
      }

      setSuccess(t('toast.taskCreated'));
      showToast(t('toast.taskCreated'), 'success');
      window.dispatchEvent(new Event('notifications:refresh'));
      setError('');
      setFormData({
        project_id: '',
        major_task_name: '',
        major_task_description: '',
        subtasks: [],
      });
      setShowMajorTaskForm(false);
      setShowSubtaskForm(false);
    } catch (error) {
      if (error.response?.status === 401) {
        handleExpiredSession({ navigate, companyId: user?.company?.id || 1 });
        return;
      }
const errorMessage =
        error.code === 'ECONNABORTED'
          ? 'Task creation timed out. Please try again.'
          :
        getApiErrorMessage(error, {
          fallback: t('toast.failedCreateTask'),
        });
      setError(errorMessage);
      showToast(errorMessage, 'error');
      setSuccess('');
    } finally {
      setIsCreatingTask(false);
    }
  };

  if (loading) {
    return <p className="text-text italic text-sm sm:text-base">{t('common.loading')}</p>;
  }

  return (
    <div>
      <ToastMessage toast={toast} onClose={clearToast} />
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold sm:text-3xl">
          {t('createTask.welcome', { role: user.code || (user.role === 'manager' ? 'Manager' : 'Team Member') })}
        </h1>
        <button
          onClick={() => setShowMajorTaskForm(true)}
          className="primary-btn px-3 py-1 text-sm font-medium sm:px-4 sm:py-2 sm:text-base"
        >
          <FaPlus className="inline mr-1 sm:mr-2" /> {t('createTask.createMajorTask')}
        </button>
      </div>
      {error && <p className="text-red-500 mb-3 sm:mb-4 text-sm sm:text-base">{error}</p>}
      {success && <p className="text-green-500 mb-3 sm:mb-4 text-sm sm:text-base">{success}</p>}
      {showMajorTaskForm && (
        <div className="card rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">{t('createTask.createMajorTask')}</h2>
          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-3 sm:mb-4">
              <label htmlFor="project_id" className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">{t('common.project')}</label>
              <select
                id="project_id"
                name="project_id"
                className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
                value={formData.project_id}
                onChange={handleInputChange}
                required
              >
                <option value="">{t('common.selectProject')}</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3 sm:mb-4">
              <label htmlFor="major_task_name" className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">{t('createTask.majorTaskName')}</label>
              <input
                type="text"
                id="major_task_name"
                name="major_task_name"
                className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
                value={formData.major_task_name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="mb-3 sm:mb-4">
              <label htmlFor="major_task_description" className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">{t('common.descriptionOptional')}</label>
              <textarea
                id="major_task_description"
                name="major_task_description"
                className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
                value={formData.major_task_description}
                onChange={handleInputChange}
              />
            </div>
            <div className="mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3">{t('createTask.subtasks')}</h3>
              {formData.subtasks.length === 0 ? (
                <p className="text-text italic text-sm sm:text-base">{t('createTask.noSubtasksAdded')}</p>
              ) : (
                <ul className="space-y-2 sm:space-y-3">
                  {formData.subtasks.map((subtask, index) => (
                    <li key={index} className="bg-hover-bg p-2 sm:p-3 rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center text-sm sm:text-base">
                      <div>
                        <p className="text-text"><strong>{t('common.title')}:</strong> {subtask.name}</p>
                        <p className="text-text"><strong>{t('common.assignee')}:</strong> {users.find(u => u.id === Number(subtask.assigned_to_id))?.email || t('common.na')}</p>
                        <p className="text-text">
                          <strong>Due:</strong>{' '}
                          {subtask.due_datetime
                            ? formatDateTime24(subtask.due_datetime, locale)
                            : t('common.na')}
                        </p>
                        <p className="text-text"><strong>{t('common.files')}:</strong> {subtask.assigned_files?.length || 0}</p>
                        <p className="text-text"><strong>{t('common.links')}:</strong> {subtask.assigned_links?.length || 0}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeSubtask(index)}
                        className="mt-2 text-error transition hover:opacity-80 sm:mt-0"
                      >
                        <FaTimes size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                onClick={() => setShowSubtaskForm(true)}
                className="mt-2 sm:mt-3 primary-btn px-3 sm:px-4 py-1 sm:py-2 font-medium text-sm sm:text-base"
              >
                <FaPlus className="inline mr-1 sm:mr-2" /> {t('createTask.addSubtask')}
              </button>
            </div>
            {showSubtaskForm && (
              <div className="mb-3 sm:mb-4 bg-hover-bg p-3 sm:p-4 rounded-lg">
                <h4 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3">{t('createTask.addSubtask')}</h4>
                <div className="mb-2 sm:mb-3">
                  <label htmlFor="subtask_name" className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">{t('common.title')}</label>
                  <input
                    type="text"
                    id="subtask_name"
                    name="name"
                    className="input-field w-full p-1 sm:p-2 text-text text-sm sm:text-base"
                    value={subtaskForm.name}
                    onChange={handleSubtaskInputChange}
                    required
                  />
                </div>
                <div className="mb-2 sm:mb-3">
                  <label htmlFor="subtask_description" className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">{t('common.descriptionOptional')}</label>
                  <textarea
                    id="subtask_description"
                    name="description"
                    className="input-field w-full p-1 sm:p-2 text-text text-sm sm:text-base"
                    value={subtaskForm.description}
                    onChange={handleSubtaskInputChange}
                  />
                </div>
                <div className="mb-2 sm:mb-3">
                  <label htmlFor="subtask_assignee" className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">{t('common.assignee')}</label>
                  <input
                    type="text"
                    id="subtask_assignee"
                    list="subtask_assignee_options"
                    className="input-field w-full p-1 sm:p-2 text-text text-sm sm:text-base"
                    placeholder={t('common.searchAssignees')}
                    value={assigneeSearch}
                    onChange={(e) => handleAssigneeSearch(e.target.value)}
                    onBlur={handleAssigneeBlur}
                    autoComplete="off"
                    required
                  />
                  <datalist id="subtask_assignee_options">
                    {filteredUsers.map(assignee => (
                      <option
                        key={assignee.id}
                        value={assignee.email}
                        label={`${getAssigneeName(assignee)} - ${assignee.email}`}
                      />
                    ))}
                  </datalist>
                </div>
                <div className="mb-2 sm:mb-3">
                  <label className="block text-text mb-2 text-sm sm:text-base">Task Repeat:</label>
                  <div className="flex flex-wrap gap-2">
                    {weekDays.map(day => {
                      const isSelected = subtaskForm.recurring_days.includes(day.value);
                      return (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleRecurringDay(day.value)}
                          className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                            isSelected
                              ? 'border-primary bg-primary text-white'
                              : 'border-secondary bg-background text-text hover:bg-hover-bg'
                          }`}
                        >
                          {day.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="mb-2 sm:mb-3">
                  <label
                    htmlFor="subtask_due_datetime"
                    className="block text-text mb-1 sm:mb-2 text-sm sm:text-base"
                  >
                    {t('common.dueDateTime')}
                  </label>

                  <input
                    type="datetime-local"
                    id="subtask_due_datetime"
                    name="due_datetime"
                    className="input-field w-full p-1 sm:p-2 text-text text-sm sm:text-base"
                    value={subtaskForm.due_datetime}
                    onChange={handleSubtaskInputChange}
                    min={formatForDateTimeLocal(new Date())}
                    required
                  />
                  {recurringDueDates.length > 0 && (
                    <div className="mt-2 rounded-lg border border-secondary/30 bg-background p-2">
                      <p className="mb-2 text-xs font-medium text-secondary-text sm:text-sm">
                        Repeat due dates that will be created:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {recurringDueDates.map(item => (
                          <span
                            key={`${item.dayValue}-${item.due_datetime}`}
                            className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary sm:text-sm"
                          >
                            {item.label}: {formatDateTime24(item.due_datetime, locale)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="mb-2 sm:mb-3">
                  <label htmlFor="subtask_priority" className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">{t('common.priority')}</label>
                  <select
                    id="subtask_priority"
                    name="priority"
                    className="input-field w-full p-1 sm:p-2 text-text text-sm sm:text-base"
                    value={subtaskForm.priority}
                    onChange={handleSubtaskInputChange}
                    required
                  >
                    <option value="low">{t('task.low')}</option>
                    <option value="medium">{t('task.medium')}</option>
                    <option value="high">{t('task.high')}</option>
                  </select>
                </div>
                <div className="mb-2 sm:mb-3">
                  <label htmlFor="assigned_files" className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">{t('createTask.attachFilesOptional')}</label>
                  <input
                    type="file"
                    id="assigned_files"
                    name="assigned_files"
                    multiple
                    className="input-field w-full p-1 sm:p-2 text-text text-sm sm:text-base"
                    onChange={handleSubtaskInputChange}
                  />
                  <p className="text-xs sm:text-sm text-secondary-text mt-1">{t('createTask.maxFilesHelp')}</p>
                  {subtaskForm.assigned_files.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs sm:text-sm text-text">
                      {subtaskForm.assigned_files.map((file, index) => (
                        <li key={`${file.name}-${index}`} className="flex items-center justify-between">
                          <span className="truncate">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeSubtaskFile(index)}
                            className="text-xs text-error transition hover:opacity-80 sm:text-sm"
                          >
                            {t('common.remove')}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="mb-2 sm:mb-3">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <label className="block text-text text-sm sm:text-base">{t('createTask.attachLinksOptional')}</label>
                    <button
                      type="button"
                      onClick={addSubtaskLink}
                      className="text-xs text-accent-blue transition hover:opacity-80 sm:text-sm"
                    >
                      {t('common.addLink')}
                    </button>
                  </div>
                  <p className="text-xs sm:text-sm text-secondary-text mb-2">{t('createTask.maxLinksHelp')}</p>
                  {subtaskForm.assigned_links.length === 0 && (
                    <p className="text-xs sm:text-sm text-text italic">{t('createTask.noLinksAdded')}</p>
                  )}
                  <div className="space-y-2">
                    {subtaskForm.assigned_links.map((link, index) => (
                      <div key={`assigned-link-${index}`} className="flex items-center space-x-2">
                        <input
                          type="url"
                          className="input-field w-full p-1 sm:p-2 text-text text-sm sm:text-base"
                          value={link}
                          onChange={(e) => updateSubtaskLink(index, e.target.value)}
                          placeholder="https://example.com"
                        />
                        <button
                          type="button"
                          onClick={() => removeSubtaskLink(index)}
                          className="text-xs text-error transition hover:opacity-80 sm:text-sm"
                        >
                          {t('common.remove')}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addSubtask}
                  className="w-full primary-btn p-1 sm:p-2 font-medium text-sm sm:text-base"
                >
                  {t('createTask.addSubtask')}
                </button>
              </div>
            )}
            {formInvalidReason && (
              <p className="mb-2 text-sm text-error">{formInvalidReason}</p>
            )}
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
              <button
                type="submit"
                disabled={isCreatingTask}
                className={`w-full p-2 sm:p-3 font-medium text-sm sm:text-base rounded-lg ${
                  !isCreatingTask
                    ? 'primary-btn'
                    : 'bg-gray-400 cursor-not-allowed text-gray-700'
                  }`}
              >
                {isCreatingTask ? t('createTask.creatingTask') : t('createTask.submitMajorTask')}
              </button>
              <button
                type="button"
                onClick={() => setShowMajorTaskForm(false)}
                className="secondary-btn w-full text-sm sm:text-base"
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}
      {user.role === 'manager' && teamMembersWithTasks.length > 0 && (
        <div className="card rounded-lg p-4 sm:p-6 mt-4 sm:mt-6">
          <h2 className="section-title mb-3 sm:mb-4">{t('createTask.teamWorkloadOverview')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {teamMembersWithTasks.map(member => (
              <div
                key={member.id}
                className="subtle-panel group relative overflow-hidden rounded-2xl border border-border p-3 shadow-card transition duration-200 hover:-translate-y-0.5 hover:shadow-card sm:p-4"
              >
                <div className="relative z-10">
                  <h3 className="mb-2 text-md font-medium text-text transition-colors duration-200 group-hover:text-primary sm:text-lg">
                    {member.email} <span className="ml-2 rounded-full bg-primary/10 px-2 py-1 text-sm font-bold text-primary">{member.taskCount}</span>
                  </h3>
                  <div className="absolute inset-0 bg-primary/5 opacity-0 transition-opacity duration-200 group-hover:opacity-100"></div>
                </div>
                {member.tasks.length > 0 ? (
                  <ul className="relative z-10 list-none space-y-1 text-sm text-text sm:text-base">
                    {member.tasks.map((task, index) => (
                      <li key={index} className="flex items-center transition-colors duration-200 hover:text-primary">
                        <span className="mr-2 h-2 w-2 rounded-full bg-primary"></span>{task}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="relative z-10 text-sm text-secondary-text sm:text-base">{t('createTask.noOngoingTasks')}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateTask;
